import * as React from 'react';
let Highcharts: any = require('highcharts');
import ReactHighstock from 'react-highcharts';
import {Button, ButtonGroup} from 'react-bootstrap';
import * as _ from 'underscore';
import moment from 'moment';

import {TEXT_ERROR_NO_READINGS, TEXT_RESET_ZOOM} from "../model/locale";


export const highstockConfigDefault = {
    global: {
        useUTC: false,
    },
    lang: {
    },
    noData: {
        position: {
            "x": 0,
            "y": 0,
            "align": "center",
            "verticalAlign": "middle"
        },
    },
    exporting: {
        enabled: false
    },
    xAxis: {
        type: "datetime",
        gridZIndex: 3,
        showEmpty: true
    },
    yAxis: [
        {title: {text: ""}, opposite: false, showEmpty: true},
        {title: {text: ""}, opposite: true, showEmpty: true},
    ],
    chart: {
        renderTo: "detailed-chart",
        height: 250,
        backgroundColor: 'transparent',
        type: 'spline',
        resetZoomButton: {
            position: {
                x: -15,
                y: 0
            },
            relativeTo: 'detailed-chart'
        },
        zoomType: 'x',
        reflow: true,
        loading: true
    },
    title: {text: ''},
    navigator: {enabled: false},
    credits: {enabled: false},
    rangeSelector: {enabled: false},
    scrollbar: {enabled: false},
    plotOptions: {
        series: {
            animation: false,
            connectNulls: true,
            marker: {
                enabled: false
            },
            dataGrouping: {
                enabled: false
            }
        },
        line: {
            animation: false,
            marker: {
                enabled: false
            }
        }
    },
    subtitle: {
        style: {
            color: '#fff'
        }
    },
    legend: {
        enabled: false
    },
    series: [],
    tooltip: {
        useHTML: true,
        // injects custom HTML for tooltip into HighStock chart
        formatter: function () {
            let tooltipHolder = document.createElement('div');
            tooltipHolder.innerHTML = '<div>' + moment.unix(Math.floor(this.x / 1000)).format("lll") + '</div>';
            for (let i = 0; i < this.points.length; i++) {
                tooltipHolder.innerHTML += '<br /><div style=" color: ' + this.points[i].series.color
                    + '">' + this.points[i].series.name + ': </div><div>' + this.points[i].y.toFixed(1)
                    + '' + this.points[i].series.tooltipOptions.valueSuffix + '</div>';
            }
            return tooltipHolder.innerHTML;
        },
        followTouchMove: true,
        borderColor: "#0086d0",
        shadow: false,
        shared: true,
        style: {
            color: '#888',
            fontSize: '9pt',
            padding: '8px',
            fontWeight: 'bold'
        }
    }
};

function getUMs(sensors) {
    return _.uniq(
        _.map(sensors,
            (s: any) => s.unit_of_measurement),
        (u) => u.um_abbreviated);
}

function getYAxisList(sensors): any {
    return _.map(getUMs(sensors), function (um: any, index) {
        return {
            // um_variable is sometimes blanks, not realiable
            // um.id would have been obvious choice, but this the id of
            // the sensor->um map row, not the um's id
            id: um.um_variable || um.um_full,
            title: {text: um.um_variable || um.um_full},
            labels: {format: "{value}" + um.um_abbreviated},
            opposite: index % 2 == 1
        };
    });
}

export interface LineChartProps{
    series: Array<any>;
    details: any;
    textForKey: Function;
    noData?: String;
    resetZoom?: String;
    resetZoomTitle?: String;
    global?: any;
}

export class LineChart extends React.Component<LineChartProps, any>{
    constructor(props){
        super();
        let lang = {
                noData: props.noData || props.textForKey(TEXT_ERROR_NO_READINGS),
                resetZoom: props.resetZoom || props.textForKey(TEXT_RESET_ZOOM),
                resetZoomTitle: props.resetZoomTitle || props.textForKey(TEXT_RESET_ZOOM)
            },
            global = props.global || highstockConfigDefault.global;
        Highcharts.setOptions({global:global,lang:lang});
    }

    render(){
        let {series, textForKey, details} = this.props;
        let config = highstockConfigDefault;
        config.series = series;
        config.yAxis = getYAxisList(details.sensors);
        return <div className="chart" ref="chart-holder">
            <ReactHighstock config={config} ref="chart"/>;
        </div>;
    }
}

export class LineChartWithLegend extends React.Component<LineChartProps, any>{
    state: {
        selectedIds: any;
    };
    constructor(props){
        super();
        let lang = {
                noData: props.noData || props.textForKey(TEXT_ERROR_NO_READINGS),
                resetZoom: props.resetZoom || props.textForKey(TEXT_RESET_ZOOM),
                resetZoomTitle: props.resetZoomTitle || props.textForKey(TEXT_RESET_ZOOM)
            },
            global = props.global || highstockConfigDefault.global;
        Highcharts.setOptions({global:global,lang:lang});
        this.state = {
            selectedIds: [0],
        };
    }

    toggleLine(id){
        let chart = (this.refs['chart'] as any).getChart();
        let serie: any= _.find(chart.series, (serie: any) => {
                return serie.options.legendIndex == id;
            }),
            contains = _.contains(this.state.selectedIds, id);
        if (serie && contains){
            serie.hide();
            let new_state =_.reject(this.state.selectedIds, function (i) {return i == id;});
            this.setState({
                selectedIds: new_state
            });
        } else if (serie && !contains) {
            serie.show();
            this.state.selectedIds.push(id);
            this.setState({
                selectedIds: this.state.selectedIds
            });
        }
    }
    buildLegend(){
        let {series} = this.props;
        let legend: any = _.map(series, (item: any) => {
            return <Button
                type="button"
                className={"legend-row " + item.colorClass + " " + (_.contains(this.state.selectedIds, item.legendIndex) ? "selected" : "") + " index"+item.legendIndex}
                onClick={() => {this.toggleLine(item.legendIndex)}}
            >
                <div className="legend-column wider"> {/*TODO: Make the text inside 'text-vertical' so it goes onto next line*/}
                    {item.name.length > 14 ? item.name.slice(0,11) + "..." : item.name }
                </div>
                <div className="legend-column">
                    {item.data.length > 0 ? item.data[item.data.length - 1][1] : "--"}{item.tooltip.valueSuffix}
                </div>
                <div className="legend-column">
                    MIN {!(item.sensor.min_temp === null) ? item.sensor.min_temp : "--"}{item.tooltip.valueSuffix}
                    <br />
                    MAX {!(item.sensor.max_temp === null) ? item.sensor.max_temp : "--"}{item.tooltip.valueSuffix}
                </div>
            </Button>
        });
        return <ButtonGroup className="legend" vertical>{legend}</ButtonGroup>;
    }
    render(){
        let {series, textForKey, details} = this.props;
        let config = highstockConfigDefault;
        config.series = series;
        config.yAxis = getYAxisList(details.sensors);
        return <div className="row detailed-chart">
            <div className="chart">
                <ReactHighstock config={config} ref="chart"/>
            </div>
            {this.buildLegend()}
        </div>;
    }
}
